/**
 * TRF-only turns for the core MessageList (chat/voice parity #1731, Task 2.2).
 *
 * Ported branch-for-branch from components/chat/MessageList.tsx's companion
 * (non-TRFer) view: a case manager's message renders as TrferBubble, a
 * handoff lifecycle system event as the centred pill, and a handoff message
 * carrying translation metadata as the translated bubble. Everything else
 * returns undefined and the core renders its own bubble. The TRFer-side
 * perspective (CounterpartTurn / SystemNote) is not here: those surfaces
 * keep TRF's MessageList until phase 9.
 */
import type { ReactNode } from 'react';
import i18n from 'i18next';
import type { Message } from '@core/chat/types';
import type { ChatMessage } from '@/types/api';
import { hasTranslationMetadata } from '@/lib/translation-utils';
import { TranslatedMessageBubble } from '@/components/handoff/TranslatedMessageBubble';
import { TrferBubble } from '@/features/chat-trf/handoff/TrferBubble';
import { HandoffSystemEvent } from '@/features/chat-trf/handoff/HandoffSystemEvent';
import { SystemNote } from '@/components/chat/SystemNote';
import { trfRowOf } from '../adapter/messages';

export interface TrfTurnContext {
  conversationId: string;
  companionName: string;
  handoffId?: string;
  viewerLanguage?: string;
}

const HANDOFF_EVENTS = new Set(['handback', 'reassign', 'handoff_initiated', 'trfer_joined']);

export function renderTrfTurn(ctx: TrfTurnContext): (message: Message, index: number) => ReactNode | undefined {
  return (message) => {
    const row = trfRowOf(message);
    if (!row) return undefined;

    if (row.role === 'trfer') {
      return (
        <TrferBubble
          content={row.content}
          authorName={row.metadata?.trfer_name ?? i18n.t('chat:handoff.caseManager')}
          metadata={row.metadata as Record<string, unknown> | undefined}
          viewerLanguage={ctx.viewerLanguage}
        />
      );
    }

    const eventKind = row.role === 'system' ? row.metadata?.event : undefined;
    // #2379: the line a receipt sent from chat leaves is a neutral note, not
    // a companion turn (no name label, no bookmark on a never-stored id).
    if (eventKind === 'receipt_sent') {
      return <SystemNote content={row.content} decision="chat-receipt-thread-note" />;
    }

    if (eventKind && HANDOFF_EVENTS.has(eventKind)) {
      return (
        <HandoffSystemEvent
          kind={
            eventKind === 'reassign' ? 'reassigned'
              : eventKind === 'handoff_initiated' || eventKind === 'trfer_joined' ? 'joined'
              : 'handback'
          }
          trferName={row.metadata?.to_trfer_name ?? row.metadata?.trfer_name}
          companionName={ctx.companionName}
        />
      );
    }

    if (ctx.handoffId && hasTranslationMetadata(row.metadata as Record<string, unknown> | undefined)) {
      return (
        <TranslatedMessageBubble
          message={row as unknown as ChatMessage}
          viewerLanguage={ctx.viewerLanguage ?? 'en'}
          handoffId={ctx.handoffId}
          conversationId={ctx.conversationId}
        />
      );
    }

    return undefined;
  };
}
